
export const moduleLabels = [
  "adaptability", "self_efficacy", "active_listening",
	"empathy", "time_management", "stress_management",
	"leadership", "motivation", "planning", "decision_making",
	"problem_solving", "learning_strategy", "social_desirability"
] as const;

export type ModuleLabel = typeof moduleLabels[number];

export const weightingModules = ["adaptability",  "self_efficacy",  "active_listening", 
	"empathy",  "time_management",  "stress_management",  
	"planning",  "decision_making",  "problem_solving", // "learning_strategy", "leadership", "motivation"
] as const;

export type WeightingModule = typeof weightingModules[number];

export type Environment = "planet_station" | "construction_site";

export type ReportType = "simplified" | "complete";

// Assessment used by campaigns with campaign_version < 4.0.0
export interface LegacyAssessment {
	environment?: Environment;
	modules?: ModuleLabel[];
	weighting: { // only applies to scores, not all modules
		required: WeightingModule[];
		primary: WeightingModule[];
		secondary: WeightingModule[];
		ignored: WeightingModule[];
	} | "neutral", // in case of neutral : "weighting": "neutral"
}

// ----- Assessment used by campaigns with campaign_version >= 4.0.0 -----

export type EnvironmentV4 = "studio";

export type SituationProvider = "eleven_labs";

export type Polarity = "positive" | "negative";

export interface SituationSidecar {
	schema_version: string; // "1"
	provider: SituationProvider;
	agent_id: string;
}

export interface SituationApp {
	schema_version: string; // "1"
	meta_human: string; // "gerard"
	environment: EnvironmentV4;
}

export interface SituationUI {
	schema_version: string; // "1"
	name: string,
	description: string; // Exercise instructions
	persona: string; // Gérard
	persona_job: string; // Architecte
	persona_profile_picture: string; // gerard.png
	available: boolean;
}

export interface AssessmentSituation {
	id: string; // "situation_x"
	max_attempts?: number | null; // 1 by default, unlimited when undefined, null or 0
	sidecar: SituationSidecar;
	ui: SituationUI;
	app: SituationApp;
}

export interface ScoreWarning {
	below_score: number; // 0.2
	message: string;
}

export interface MetricWarning {
	below_rating: number; // 1
}

export interface SkillMissing {
	message: string;
}

export type SkillDisplay =
	| { type: "skill" }
	| { type: "warning" }
	| { type: "behavior"; left_label: string; right_label: string }; // "distant" <-> "chaleureux"

export interface AssessmentMetric {
	id: string; // "metric_a"
	name: string;
	description: string; // multiparagraphs
	scoring_instructions: string; // multiparagraphs
	weight: number;
	polarity?: Polarity;
	raise_warning?: MetricWarning;
	raise_missing?: boolean; // false by default
	required?: boolean; // false by default
}

export interface AssessmentSkill {
	id: string; // "skill_x"
	situations: AssessmentSituation["id"][];
	name: string;
	display: SkillDisplay;
	description: string; // multiparagraphs
	scoring_instructions: string; // multiparagraphs
	weight: number;
	polarity?: Polarity;
	raise_warning?: ScoreWarning;
	raise_missing?: SkillMissing; // ignored if absent
	required?: boolean; // false by default
	metrics: AssessmentMetric[];
}

export interface AssessmentV4 {
	description: string;
	situations: AssessmentSituation[];
	raise_warning?: ScoreWarning;
	skills: AssessmentSkill[];
}

export interface CampaignCreation {
	schema_version: string,
  campaign_version: string,
  variant?: "full" | "essentials",
	organization_id: string;
	campaign: {
		parent_workspace_id: string; // "019ae519-a66a-7016-846b-2afc0a198715", // prepare for nested folders/workspaces
		internal_name: string; // "Bee management issue (associate xyz)"
		headline: string; // "Looking for a (bee)keeper",
		tagline: string; // "<p>we want to bee with you</p>", // BAD BAD: HTML injection!!
		job_title: string; // beekeeper,
		locale?: string; // en-US
		force_locale?: boolean;
	},
	branding: {
		banner_image_id: string; //"019ae525-8490-798e-b33c-f3051a532a52",
		background_image_id: string; // "019ae525-c4ca-7708-be3b-d6878a678eb8",
		text_color: string; //"#FFFFFF", // #FFFFFF or #000000 only ?
		highlight_color: string; // "#1234AB",
	},
	assessment: LegacyAssessment,
	surveys?: {
		satisfaction: boolean;
		demographic: boolean;
	},
	documents?: {
		certification: {
			send_to_talent: boolean;
		},
		report: {
			send_to_talent: boolean;
			format: ReportType;
		}
	},
}

export type Campaign = CampaignCreation & {
  created_at: string,
	updated_at: string,
  id: string;
  public_invite_id: string;
  archived: boolean;
  auto_activate: boolean;
}

// campaign_version >= 4.0.0
export type CampaignV4 = Omit<Campaign, "assessment"> & {
	assessment: AssessmentV4;
}

export interface CompletedSituation {
	situation: AssessmentSituation["id"]; // "situation_x"
	object: string; // transcript object key in S3, "date_conv.json"
}

// Participant progress for campaign_version >= 4.0.0
export interface TrackingV4 {
	schema_version: string,
	campaign_version: string; // "4.0.0"
	total_situations: AssessmentSituation["id"][];
	completed_situations: CompletedSituation[];
}

export interface CampaignContext {
  organisationId: string;
	spaceId: string;
  campaignId: string;
}

export type CampaignRow = {
	index: number,
	id: string,
	name: string,
	job: string,
	date: string,
}